import { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import useAppStore from '../stores/app';
import useDataBroker from '../stores/dataBroker';
import config, { ROUTES } from '../config';
import PageLoader from '../helpers/loader';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { authenticate, setRoomSid, room } = useAppStore();
  const [loading, setLoading] = useState(true);

  const check = async (options = {}) => {
    //console.log('Start: Authenticating...');

    // eslint-disable-next-line no-prototype-builtins
    if (!options.hasOwnProperty('redirectOnFailure')) {
      options.redirectOnFailure = true;
    }

    // Update query params if they are in the URL
    const storedToken = sessionStorage.getItem(
      config?.vtc?.authentication?.localStorageToken
    );
    const url = new URL(window.location.href);
    const params = new Proxy(new URLSearchParams(url.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    });

    const authenticateResult = await authenticate(params?.token || storedToken);

    if (authenticateResult.successful) {
      // now, check to see if the room is passed in or set...
      const storedRoomSid = sessionStorage.getItem(
        config.vtc.authentication.localStorageRoomSid
      );
      const url = new URL(window.location.href);
      const params = new Proxy(new URLSearchParams(url.search), {
        get: (searchParams, prop) => searchParams.get(prop),
      });

      const roomSid =
        params?.proctorRoomSid || params?.roomSid || storedRoomSid;

      // only set the room if it is different than the room that is already configured and is not null...
      if (roomSid !== room?.sid && roomSid) {
        //console.log("Configuring Room...");
        const setRoomSidResult = await setRoomSid(roomSid);

        if (!setRoomSidResult.successful) {
          //TODO: Auth failed...do something...
          console.log('Setting Room Sid Failed....');
        }
      }

      setLoading(false);

      return { successful: true, roomSid };
    } else {
      if (!options?.redirectOnFailure) {
        setLoading(false);
        return { successful: false };
      } else {
        // the user was not authenticated, so send them to the login screen.
        window.location = '/';
      }
    }
  };

  useEffect(() => {
    // indicates where the data should be persisted, this can be either local for testing or remote...
    useDataBroker.getState().setDataMode(config.vtc.mode);

    // check the current route and if login we do not need to check...
    if (window.location.pathname !== ROUTES.LOGIN) {
      check();
    }
  }, []);

  if (
    window.location.pathname !== ROUTES.LOGIN &&
    window.location.pathname !== ROUTES.AUTO_LOGIN
  ) {
    if (loading) {
      return PageLoader({ text: 'Authenticating...' });
    }
  }

  return (
    <AuthContext.Provider value={{ check }}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export const ProtectedRoute = ({ children }) => {
  const { check } = useAuth();
  const [isAuthorized, setIsAuthorized] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Define the async function inside the effect.
    const verifyAccess = async () => {
      const checkResult = await check();
      setIsAuthorized(checkResult.successful);
    };

    verifyAccess();
  }, [navigate]);

  if (!isAuthorized) return 'Checking...';

  return children;
};
